import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useNavigate } from 'react-router-dom';
import '../styles/Leads.css';
import { SortArrows, Zillow, Redfin, Realtor, Image, ExternalLink, Warning } from '../assets/icons';
import { FaviconImg } from './SourceIcon';
import { getFaviconSrc, getSiteName } from '../lib/sourceFaviconUtils';
import { colour, formatterLong, numberFormatter } from '../styles/GlobalStyles';
import '../styles/Triage.css';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, MeasuringStrategy } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, IconButton, Input, Tooltip, ExitScreen, ProviderIcon } from './';
import { getFirestore, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueLeadEdit } from '../hooks/useLeadMutation';
import { prefetchLeadDetail } from '../hooks/useLeadFetch';
import { normalizeAssignmentData, getRoleLabels } from '../utils/assignmentUtils';
import { cloudFunctionV2 } from '../functions/api';
import { getDealStatuses } from '../utils/dealStatusUtils';
import { getImageFolderProvider, isUnsupportedImageProvider, deriveDisplayImageUrls, isMapOrStreetViewImage, arePhotosPending } from '../utils/imageUtils';
import { deriveViewOnlyFolder } from '../lib/photoSources';
import { getLeadSourceBgClass, resolveLeadSourceProfile } from '../lib/leadSource';
import SourceIcon from './SourceIcon';
import { pickMainEntry, hasLowerPricedSibling, getSenderDisplay, resolveSenderLogo, resolveIngestedAskingPrice } from '../utils/leadSelectionUtils';
import SenderLogo from './SenderLogo';
import { ArvCell, RepairsCell } from './CalcStatus';
import { resolveArvStatus, resolveRepairsStatus, isUnderwritingPending, CALC_STATES, isLeadWorking } from '../lib/underwritingStatus';
import { DISTRESS_FIELD_NAMES } from '../utils/distressSignals';

const DISTRESS_PRIORITY = Object.fromEntries(DISTRESS_FIELD_NAMES.map((k, i) => [k, i]));
const DISTRESS_VISIBLE_LIMIT = 2;
const MOTIVATION_VISIBLE_LIMIT = 1;

// Inline controls inside a row (links, the image button, editable cells, the
// entries badge, the actions kebab) handle their own clicks — pressing one is
// not an attempt to open the lead. Shared by the row's mousedown primer and its
// click handler so the two can never disagree about what counts as opening a
// row, which would otherwise show up as reads fired for clicks that never
// navigate.
const ROW_INLINE_CONTROL_SELECTOR = [
    '.leads-link-icon',
    '.leads-image-button',
    '.leads-edit-container',
    '.leads-entries-badge',
    '.leads-editable-cell-clickable',
    '.leads-actions-cell',
].join(',');

function opensTheRow(e) {
    return !e.target.closest(ROW_INLINE_CONTROL_SELECTOR);
}

// Deal status configuration: key → { label, bg, dot }
const DEAL_STATUS_CONFIG = {
    watch_list:                   { label: 'Watch List',                    bg: 'bg-colour-yellow-01',  dot: colour.yellow02 },
    qualified_find_buyer:         { label: 'Qualified – Find Buyer',        bg: 'bg-colour-jade-01',    dot: colour.jade02   },
    qualified_mgmt_review:        { label: 'Qualified – Mgmt Review',       bg: 'bg-colour-orange-01',  dot: colour.orange02 },
    qualified_internal_purchase:  { label: 'Qualified – Internal Purchase', bg: 'bg-colour-purple-01',  dot: colour.purple02 },
    unqualified_failed_uw:        { label: 'Unqualified – Failed UW',       bg: 'bg-colour-red-01',     dot: colour.red02    },
    unqualified_unavailable:      { label: 'Unqualified – Unavailable',     bg: 'bg-colour-gray-02',    dot: colour.gray04   },
};

// Ordered list for dropdown options
const DEAL_STATUS_OPTIONS = [
    { label: 'No status',                     value: null },
    { label: 'Watch List',                    value: 'watch_list' },
    { label: 'Qualified – Find Buyer',        value: 'qualified_find_buyer' },
    { label: 'Qualified – Mgmt Review',       value: 'qualified_mgmt_review' },
    { label: 'Qualified – Internal Purchase', value: 'qualified_internal_purchase' },
    { label: 'Unqualified – Failed UW',       value: 'unqualified_failed_uw' },
    { label: 'Unqualified – Unavailable',     value: 'unqualified_unavailable' },
];

// Editable fields configuration (image is handled via modal, not inline)
const EDITABLE_FIELDS = {
    beds: { path: 'verifiedCharacteristics.bedrooms', type: 'integer', min: 0 },
    baths: { path: 'verifiedCharacteristics.bathrooms', type: 'number', min: 0 },
    sqft: { path: 'verifiedCharacteristics.livingSquareFeet', type: 'integer', min: 1 }
};

// Portfolio badge colour palette — deterministic per portfolioId
const PORTFOLIO_COLOURS = [
    { bg: 'bg-colour-purple-01', text: 'colour-purple-03' },
    { bg: 'bg-colour-blue-01', text: 'colour-blue-03' },
    { bg: 'bg-colour-jade-01', text: 'colour-jade-03' },
    { bg: 'bg-colour-orange-01', text: 'colour-orange' },
    { bg: 'bg-colour-pink-01', text: 'colour-pink-03' },
    { bg: 'bg-colour-aqua-01', text: 'colour-aqua-03' },
    { bg: 'bg-colour-magenta-01', text: 'colour-magenta-03' },
];

const getPortfolioColour = (portfolioId) => {
    if (!portfolioId) return PORTFOLIO_COLOURS[0];
    let hash = 0;
    for (let i = 0; i < portfolioId.length; i++) {
        hash = ((hash << 5) - hash + portfolioId.charCodeAt(i)) | 0;
    }
    return PORTFOLIO_COLOURS[Math.abs(hash) % PORTFOLIO_COLOURS.length];
};

// Does this row render the deal-badges line — now just the blue buyer-match chip — under the
// address? Used only to seed the virtualizer's row-height estimate so a badge row reserves its
// taller height on first paint (see estimateSize).
//
// It used to also return true on any `strategyFit`, back when strategy and floor-plan chips
// shared this line. Those were removed on 2026-08-11, and leaving that clause would have
// reserved the taller height for nearly every lead in the table — strategyFit is populated on
// all of them — padding out rows that now render no badge at all.
//
// Matched-buyer count for a row. Reads the denormalized data.buyerMatchCount that
// useLeadsQuery projects; data.buyerMatchScores is NOT projected here any more (it is
// unbounded — see the note in that hook), so the key-count fallback only fires for a
// caller that supplies the map itself. A lead written before the count existed reads 0
// until scripts/backfillBuyerMatchCount.js runs.
const matchedBuyerCount = (item) => {
    if (typeof item?.buyerMatchCount === 'number') return item.buyerMatchCount;
    return item?.buyerMatchScores ? Object.keys(item.buyerMatchScores).length : 0;
};

const rowHasDealBadges = (item) => {
    if (!item) return false;
    return matchedBuyerCount(item) > 0;
};

// Standard column definitions
//
// "Status" column shows the outreach/CRM review state (new/viewed/sent/unqualified),
// renamed in the label below from "Status" → "Outreach" to disambiguate from the
// pipeline processing status (queued/processing/complete/failed) now surfaced
// in the activity rail.
//
// The "hot deal" concept was removed entirely — the legacy triageAction column,
// the priority "Score" column, and the inline flame badge are all gone. Deal value
// is now shown as Stage-2 strategy-fit badges (Flip / Co-living pass·fail·review) plus
// a wholesale buyer-match chip, rendered under the address (see renderCell address branch).
/**
 * OUR price — the one number actually offered (functions/utils/ourPrice.js; decisions 0106 and 0107): the
 * BUYER's price when the lead carries one (inside a pricing buyer's zones, decision 0109), else the
 * SLIDING MAO when the lead carries one (a company whose switch is on), else the co-living TURNKEY MAO on a turnkey house,
 * else the RESALE MAO, else the HOLD MAO, each less our wholesale fee
 * (`coLivingWholesaleFee`), else the fix-and-flip MAO. The client copy of the server's rule: the
 * server writes `computedMaoVsAsking` from it, so the "Our Price vs Ask" column must name the same
 * price that gap was computed from. `basis` says which of the five it is.
 * ⚠️ Keep in step with functions/utils/ourPrice.js — a drift shows one price beside another's gap.
 * A price of 0 or less (the fee included) is no price at that basis and falls through to the next.
 */
const finiteOrNull = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
// On a buyer's price, the fee struck with it (the server's ourPrice.js#buyerFeeOn): an older rank stored none on the lead.
export const buyerFeeOf = (d) => {
    const f = finiteOrNull(d && d.coLivingBuyerMaoFor && d.coLivingBuyerMaoFor.wholesaleFee);
    return f != null ? Math.max(0, f) : (positiveOrNull(d && d.coLivingWholesaleFee) ?? 0);
};
export function ourPriceOf(item) {
    const d = item || {};
    const fee = positiveOrNull(d.coLivingWholesaleFee) ?? 0;
    const net = (v) => { const p = positiveOrNull(v); return p == null ? null : positiveOrNull(p - fee); };
    // Inside a pricing buyer's zones his price decides, and 0 or less is no price — never a fall back (decision 0109).
    if (finiteOrNull(d.coLivingBuyerMao) != null) {
        const p = positiveOrNull(d.coLivingBuyerMao);
        const buyer = p == null ? null : positiveOrNull(p - buyerFeeOf(d));
        return buyer != null ? { price: buyer, basis: 'buyer' } : { price: null, basis: null };
    }
    // The sliding price decides whenever it exists, and one of 0 or less is no co-living price at all — never a fall back
    // to the others (the server's rule; its rent check just rejected them).
    if (finiteOrNull(d.coLivingSlidingMao) != null) {
        const sliding = net(d.coLivingSlidingMao);
        return sliding != null ? { price: sliding, basis: 'sliding' } : { price: null, basis: null };
    }
    // On a turnkey house the turnkey price decides, even below the resale one, and a turnkey price of 0 or
    // less is no co-living price at all — never the 80% one. Co-living, not flipping: see the server's rule.
    if (finiteOrNull(d.coLivingTurnkeyMao) != null) {
        const turnkey = net(d.coLivingTurnkeyMao);
        if (turnkey != null) return { price: turnkey, basis: 'turnkey' };
    } else {
        const resale = net(d.coLivingCtvMao);
        if (resale != null) return { price: resale, basis: 'resale' };
        const hold = net(d.coLivingMao);
        if (hold != null) return { price: hold, basis: 'hold' };
    }
    const flip = positiveOrNull(d.underwriting?.maxAllowableOffer);
    if (flip != null) return { price: flip, basis: 'flip' };
    return { price: null, basis: null };
}

/**
 * The SELLER's ask — the ingested one first, never the calculator-editable box when there is one.
 * A non-positive ask is NO ask (a stamped 0 is "unknown", not "free"), so each is taken only if > 0 —
 * the same rule as the server's `askOf` (functions/utils/ourPrice.js).
 */
const positiveOrNull = (v) => { const n = finiteOrNull(v); return n != null && n > 0 ? n : null; };
export const sellerAskOf = (item) => positiveOrNull(item?.ingestedAskingPrice) ?? positiveOrNull(item?.currentAskingPrice);

/** A price against the ask, in percent to one decimal; negative = below the ask. Same formula as the server. */
export function ourGapVsAsk(price, ask) {
    if (!Number.isFinite(price) || !Number.isFinite(ask) || ask <= 0) return null;
    return Math.round(((price - ask) / ask) * 1000) / 10;
}

/**
 * The gap a row SHOWS: computed fresh from our price and the seller's ask — or null (a dash) when
 * either is missing. 🚨 NEVER the stored `computedMaoVsAsking`: it is only ever computed from these
 * same inputs, so when they are missing now it is stale by definition, and on any lead not re-synced
 * since 2026-09-11 (and not backfilled) it is the FLIP MAO's gap — showing it beside our price is the
 * very mismatch this column was changed to end. Same rule as the server's
 * functions/mcpAPI/leadProjection.js#ourGapVsAsking. (The page is sorted server-side on the stored
 * field, so an un-backfilled row may sit a little out of order; a wrong number is worse than that.)
 */
export function gapForDisplay(item) {
    return ourGapVsAsk(ourPriceOf(item).price, sellerAskOf(item));
}

/**
 * The gap a row should show right after an inline edit of `currentAskingPrice` to `newCurrentAsk`:
 * our price against the ask AS THE SERVER WILL READ IT after the edit (sellerAskOf).
 * 🚨 The server measures against `ingestedAskingPrice` FIRST, so on a lead that has one, editing the
 * current price does not move the gap — and must not move the local copy, or the row shows a number
 * the next refresh silently takes back. Computed, never copied from the stored field (see gapForDisplay).
 */
export function gapAfterAskEdit(lead, newCurrentAsk) {
    return ourGapVsAsk(ourPriceOf(lead).price, sellerAskOf({ ...lead, currentAskingPrice: newCurrentAsk }));
}

/** How each basis is named to a person — never just "MAO", which reads as the flip number. */
export const OUR_PRICE_BASIS_LABEL = {
    buyer: "buyer's price",
    sliding: 'co-living sliding MAO',
    turnkey: 'co-living turnkey MAO',
    resale: 'co-living resale MAO',
    hold: 'co-living hold MAO',
    flip: 'fix-and-flip MAO',
};

const STANDARD_COLUMNS = [
    { key: 'leadReviewStatus', label: 'Outreach', width: '150px', sortable: true },
    { key: 'dealStatus', label: 'Deal Status', width: '200px', sortable: true },
    { key: 'address', label: 'Address', width: '200px', sortable: true },
    { key: 'links', label: 'Links', width: '140px', sortable: false },
    { key: 'image', label: 'Images', width: '150px', sortable: false },
    { key: 'beds', label: 'Beds', width: '80px', sortable: true },
    { key: 'baths', label: 'Baths', width: '80px', sortable: true },
    { key: 'sqft', label: 'Sq Ft', width: '100px', sortable: true },
    { key: 'yearBuilt', label: 'Year Built', width: '80px', sortable: true },
    { key: 'askingPrice', label: 'Asking Price', width: '130px', sortable: true },
    { key: 'zillowEstimate', label: 'Zillow Est.', width: '110px', sortable: true },
    { key: 'redfinEstimate', label: 'Redfin Est.', width: '110px', sortable: true },
    { key: 'realtorEstimate', label: 'Realtor Est.', width: '110px', sortable: true },
    { key: 'spread', label: 'Spread', width: '100px', sortable: true, tooltip: 'Median of Zillow, Redfin & Realtor estimates vs. asking price. Positive = estimates above asking.' },
    // Underwriting columns (hidden by default - users toggle via ColumnToggle)
    { key: 'arv', label: 'ARV', width: '120px', sortable: false },
    { key: 'repairCost', label: 'Repair Cost', width: '120px', sortable: false },
    { key: 'profit', label: 'Est. Profit', width: '120px', sortable: false },
    { key: 'profitPct', label: 'Profit %', width: '90px', sortable: false },
    { key: 'roi', label: 'ROI %', width: '80px', sortable: false },
    // The FIX-AND-FLIP MAO — NOT our price. It is auto-inserted beside "Our Price vs Ask", whose gap is
    // measured against our price (usually the co-living resale MAO); labelled bare "MAO", a row read
    // "MAO $120,000 | 10% below asking" with the 10% about a different number.
    { key: 'mao', label: 'Flip MAO', width: '120px', sortable: false, tooltip: 'The fix-and-flip maximum allowable offer. Not our price — Our Price vs Ask is measured against our price.' },
    // OUR price's gap vs the seller's ask — the same number the MCP and the GHL card quote (see
    // ourPriceOf above). Sorted server-side on the stored `computedMaoVsAsking`.
    { key: 'maoVsAsking', label: 'Our Price vs Ask', width: '200px', sortable: true, tooltip: "Our price vs. the seller's asking price. Our price is the co-living sliding MAO where the company uses it, else the turnkey MAO on a house in good condition, else the resale MAO, else the hold MAO, less our wholesale fee, else the fix-and-flip MAO." },
    // Co-living deal score, 0–100. Higher is better; sort DESCENDING for best-first. Base is how
    // little of a discount the deal needs (MAO ÷ ask), adjusted up or down by whether the rent
    // clears the per-room cash-flow bar at that loan.
    { key: 'dealScore', label: 'Deal Score', width: '150px', sortable: true, tooltip: 'How good the deal is overall, out of 100. Mostly how close to the asking price you can buy it and still get your cash back out, adjusted up or down by whether the rent covers the new loan payment well enough. Only shown for houses that need hard money before they can be refinanced.' },
    // The Deal Score's base on its own, with the cash-flow adjustment stripped off: purely how
    // close to the ask you can pay. Sorts DESCENDING (100% = works at ask sits top). Distinct from
    // `maoVsAsking` above, which is OUR price's signed gap vs the ask (the co-living resale MAO, else
    // the hold MAO, else the flip MAO — functions/utils/ourPrice.js); this one is the hold MAO as a
    // percent OF the ask, the refinance cash-recovery price. The two labels stay explicitly different.
    { key: 'maoVsAsk', label: 'MAO vs Ask (co-living)', width: '190px', sortable: true, tooltip: 'How close to the asking price you can pay and still get essentially all your cash back out at the refinance. 100% means it works at full asking price; 80% means you need to be 20% under. Only shown for houses that need hard money before they can be refinanced.' },
    { key: 'report', label: 'Report', width: '90px', sortable: false },
    { key: 'distressSignals', label: 'Distress Signals', width: '180px', sortable: false },
    { key: 'motivation', label: 'Motivation', width: '220px', sortable: false },
    { key: 'notes', label: 'Description', width: '200px', sortable: false },
    { key: 'acquisitionNotes', label: 'Acq. Notes', width: '200px', sortable: false },
    { key: 'portfolio', label: 'Portfolio', width: '140px', sortable: false },
    { key: 'propertyType', label: 'Property Type', width: '120px', sortable: true },
    { key: 'leadType', label: 'Lead Type', width: '120px', sortable: true },
    { key: 'assigned', label: 'Assigned', width: '160px', sortable: false },
    { key: 'source', label: 'Source', width: '110px', sortable: false, tooltip: 'How this lead reached Clozers (email inbox, X, MLS, API, manual entry).' },
    { key: 'Main contact', label: 'Main contact', width: '150px', sortable: false },
    { key: 'mainContactEmail', label: 'Main Contact Email', width: '180px', sortable: false },
    { key: 'mainContactPhone', label: 'Main Contact Phone', width: '160px', sortable: false },
    { key: 'createdAt', label: 'Date', width: '120px', sortable: true },
    { key: 'updatedAt', label: 'Last Activity', width: '120px', sortable: true },
];

// Draggable header cell component
// Sort-arrow hover is DraggableHeader's own state, not the page's. It used to
// live in Leads.js and be threaded down as hoveredColumn/onHoverColumn, so
// pointing at any column header re-rendered the entire Leads page — table, map
// and activity rail — to change one SVG fill. Local state re-renders just the
// header the pointer is actually over.
function DraggableHeader({ column, sortColumn, sortDirection, onSort }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: column.key,
        disabled: column.key === 'address' || column.key === 'leadReviewStatus' // These columns cannot be dragged
    });

    const [showTooltip, setShowTooltip] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const hoverTimer = useRef(null);

    const handleTooltipEnter = () => {
        hoverTimer.current = setTimeout(() => setShowTooltip(true), 500);
    };
    const handleTooltipLeave = () => {
        clearTimeout(hoverTimer.current);
        setShowTooltip(false);
    };

    useEffect(() => {
        return () => clearTimeout(hoverTimer.current);
    }, []);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`leads-table-cell leads-table-header-cell body-regular colour-secondary ${isDragging ? 'leads-header-dragging' : ''} ${column.tooltip ? 'relative-container' : ''}`}
            onClick={() => column.sortable && onSort(column.key)}
            onMouseEnter={() => {
                if (column.sortable) setIsHovered(true);
                if (column.tooltip) handleTooltipEnter();
            }}
            onMouseLeave={() => {
                setIsHovered(false);
                if (column.tooltip) handleTooltipLeave();
            }}
            {...attributes}
            {...listeners}
        >
            <span>{column.label}</span>
            {column.sortable && (
                <SortArrows
                    fill={sortColumn === column.key ? colour.gray06 : (isHovered ? colour.gray04 : colour.gray00)}
                    className={sortColumn === column.key ? '' : 'opacity-50'}
                />
            )}
            {column.tooltip && (
                <Tooltip
                    type="info"
                    title={column.label}
                    body={column.tooltip}
                    position="top"
                    contextTooltipBg={null}
                    exit={false}
                    exitFunc={null}
                    customClass={showTooltip ? "fade-in" : "fade-out"}
                />
            )}
        </div>
    );
}

// Row-level kebab actions menu. Trigger lives in the row (sticky right
// column); the open menu is portaled into document.body with fixed
// positioning so it can't be clipped by the table cell's overflow:hidden
// or by adjacent virtualized row wrappers. Same trick as CalcStatus.
function ActionsKebab({ isOpen, onToggle, isArchived, onArchive, onRestore }) {
    const triggerRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, right: 0 });

    // Measure position on open and whenever the page scrolls/resizes while
    // open, so the menu tracks its trigger if the table scrolls underneath.
    const measure = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setCoords({
            top: rect.bottom + 4,
            right: window.innerWidth - rect.right
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;
        measure();
        const handler = () => measure();
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', handler);
        };
    }, [isOpen, measure]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={`leads-actions-kebab${isOpen ? ' leads-actions-kebab-open' : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle();
                }}
                aria-label="Actions"
            >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="3" r="1.5" fill={colour.gray06} />
                    <circle cx="8" cy="8" r="1.5" fill={colour.gray06} />
                    <circle cx="8" cy="13" r="1.5" fill={colour.gray06} />
                </svg>
            </button>
            {isOpen && createPortal(
                <div
                    className="leads-actions-menu leads-actions-menu-portal"
                    style={{ top: coords.top, right: coords.right }}
                >
                    {isArchived ? (
                        <div
                            className="leads-actions-menu-item body-regular colour-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRestore();
                            }}
                        >Restore</div>
                    ) : (
                        <div
                            className="leads-actions-menu-item body-regular colour-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                onArchive();
                            }}
                        >Archive</div>
                    )}
                </div>,
                document.body
            )}
        </>
    );
}

// "+N" overflow pill for signal columns (distress / motivation). The popover is
// portaled to document.body and positioned from the trigger's bounding rect, so
// it escapes the table container's overflow:auto clipping and the transform-
// stacked virtualized rows. Hover/focus open it; a small close delay keeps it
// open while the cursor travels from the pill down to the popover.
function SignalOverflowChip({ chipClassName, count, items, ariaLabel }) {
    const triggerRef = useRef(null);
    const closeTimer = useRef(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, right: 0 });

    const measure = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setCoords({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }, []);

    const openNow = useCallback(() => {
        clearTimeout(closeTimer.current);
        setOpen(true);
    }, []);
    const closeSoon = useCallback(() => {
        closeTimer.current = setTimeout(() => setOpen(false), 100);
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        measure();
        const handler = () => measure();
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', handler);
        };
    }, [open, measure]);

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    return (
        <span
            ref={triggerRef}
            className={chipClassName}
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={openNow}
            onMouseLeave={closeSoon}
            onFocus={openNow}
            onBlur={closeSoon}
            aria-label={ariaLabel}
        >
            +{count}
            {open && createPortal(
                <div
                    className="signal-overflow-popover"
                    style={{ top: coords.top, right: coords.right }}
                    role="tooltip"
                    onMouseEnter={openNow}
                    onMouseLeave={closeSoon}
                >
                    {items.map((label, i) => (
                        <span key={i} className="signal-overflow-popover-item body-regular colour-primary">
                            {label}
                        </span>
                    ))}
                </div>,
                document.body
            )}
        </span>
    );
}

// Inline status dropdown (Outreach + Deal status). The option list is portaled
// to document.body and positioned from the trigger's bounding rect, for the
// same reason as ActionsKebab/SignalOverflowChip: each virtualized row carries
// a `transform: translateY(...)` (its own stacking context) and the table
// container is `overflow: auto`, so an in-DOM absolute menu gets clipped by the
// next row and the container edge. A portal escapes both. `trigger` is the
// clickable badge; `options` is [{ key, value, selected, content }].
function StatusBadgeDropdown({ isOpen, onToggle, onClose, trigger, options, onSelect }) {
    const triggerRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const measure = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setCoords({ top: rect.bottom + 4, left: rect.left });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;
        measure();
        const handler = () => measure();
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', handler);
        };
    }, [isOpen, measure]);

    return (
        <div
            className="leads-deal-status-cell relative-container"
            onClick={(e) => e.stopPropagation()}
        >
            <div
                ref={triggerRef}
                className="leads-deal-status-badge-wrapper"
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle();
                }}
            >
                {trigger}
            </div>
            {isOpen && createPortal(
                <>
                    <ExitScreen clickScreen={onClose} />
                    <div
                        className="leads-deal-status-dropdown leads-deal-status-dropdown-portal"
                        style={{ top: coords.top, left: coords.left }}
                    >
                        {options.map((opt) => (
                            <div
                                key={opt.key}
                                className={`leads-deal-status-option body-regular ${opt.selected ? 'leads-deal-status-option-selected' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(opt.value);
                                }}
                            >
                                {opt.content}
                            </div>
                        ))}
                    </div>
                </>,
                document.body
            )}
        </div>
    );
}

function LeadsTable({
    data,
    loading = false, // Whether data is still loading
    refetching = false, // Background fetch in-flight with prior data still on screen
    sortColumn,
    sortDirection,
    onSort,
    onRowClick,
    visibleColumns = null, // Array of column keys to show (null = show all standard)
    availableCustomFields = [], // Array of { key, displayName, type }
    columnOrder = null, // Array of column keys in display order
    onColumnReorder = null, // Callback when columns are reordered: (activeId, overId) => void
    companyId = null, // Company ID for saving edits
    onLeadUpdate = null, // Callback to update local state after save: (leadId, field, newValue) => void
    leadTypeFilter = null, // Active lead type filter (e.g., 'direct_to_seller')
    onEditAddress = null, // Callback to open edit address modal: (leadId, currentAddress) => void
    onOpenImageModal = null, // Callback to open image URL modal: (leadId, currentUrl) => void
    companyData = null, // Full company document data (has .users array)
    currentUserId = null, // The currently authenticated user's ID
    onArchiveLead = null, // Callback: (leadId) => void
    onRestoreLead = null, // Callback: (leadId) => void
    isArchivedView = false, // Whether we're showing archived leads
    actionsOpenForLead = null, // Which lead's actions menu is open (leadId or null)
    onActionsOpenChange = null, // Callback: (leadId | null) => void
    onOpenContactModal = null, // Callback to open identify contact modal: (leadId, rawData) => void
    page = null // Current page number; changes scroll the table back to the top
}) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const roleLabels = getRoleLabels(companyData);
    const { config: dealStatusConfig, options: dealStatusOptions } = useMemo(() => getDealStatuses(companyData), [companyData]);

    // Row hover is styled by CSS `:hover` alone — deliberately no `hoveredRow`
    // state. There used to be one, setting a `leads-table-row-hovered` class
    // that every rule already matched via an identical `:hover` twin, so it
    // bought nothing visually and cost a full re-render of every visible row on
    // each row-to-row pointer move: `renderRow` closes over the state, so all
    // ~20 virtualized rows re-ran their entry sort and their ~25 `renderCell`
    // calls just to move a background colour one row down.

    // ── Priming the lead-detail page ─────────────────────────────────────────
    // Clicking a row starts BOTH the lead-detail route chunk download and its
    // getLeadDetail round-trip from cold, so the spinner covers a full network
    // trip. Both are worth starting early — but they are NOT the same kind of
    // thing, and an earlier revision of this that primed both on hover was
    // wrong on the second one:
    //
    //   • The route chunk is a static, immutable, forever-cacheable asset. It
    //     cannot go stale and it is fetched at most once a session. Hover is a
    //     free and generous signal for it.
    //   • The lead data costs a getLeadDetail call — a lead query, an
    //     unprojected property-doc read, and (for includeCoLivingOnly) a buyer
    //     roster page. It CAN go stale. Priming that on hover meant a browse
    //     down a 50-row page issued up to 50 reads for leads nobody opened,
    //     and it opened a staleness window: the prime landed before an inline
    //     edit of the same row, and the detail page — reading the same cache
    //     entry within its 30s staleTime — then rendered the pre-edit value.
    //
    // So they split by cost. The chunk warms on hover; the data waits for
    // mousedown, which fires only for a row actually being opened and only
    // milliseconds before the navigation reads it, leaving no window for an
    // edit to land in between.
    const warmLeadDetailChunk = useCallback(() => {
        // Same chunk index.js lazy-loads for /leads/:propertyId, so this warms
        // the module cache the route is about to read from. Repeat calls
        // resolve from that cache — no timer or de-duplication needed.
        import('../pages/LeadDetailV2').catch(() => { /* best-effort */ });
    }, []);
    // Left button only: a right-click opens the context menu and a middle-click
    // does nothing on this row, so neither is about to navigate.
    const primeOnPress = useCallback((e, item, canOpen) => {
        if (e.button !== 0 || !canOpen || !opensTheRow(e)) return;
        prefetchLeadDetail(queryClient, { companyId, propertyId: item.propertyId });
    }, [companyId, queryClient]);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [dealStatusDropdownLead, setDealStatusDropdownLead] = useState(null);
    const [outreachDropdownLead, setOutreachDropdownLead] = useState(null);

    // Virtualization: only render visible rows. The scroll container is the
    // outer `.leads-table-container`; we measure each row dynamically so
    // expanded duplicate-entry rows (variable height) don't break layout.
    const scrollParentRef = useRef(null);
    const rowVirtualizer = useVirtualizer({
        count: (data && data.length) || 0,
        getScrollElement: () => scrollParentRef.current,
        // Address cell renders street + city/state on two lines (gap 2px +
        // body-semibold ~22px + label-regular ~18px + 24px y-padding ≈ 66).
        // Underestimating was leaving gaps + clipping rows whose content
        // exceeded the estimate before measureElement could correct it — a row
        // that also carries a Stage-2 deal-badges line (strategy chips / blue
        // buyers chip) is ~86px, so estimating 66 leaves the NEXT absolutely-
        // positioned row overlapping and covering the badge until measureElement
        // corrects. Reserve the taller height up front for rows that have badges
        // so the layout never clips them, even on the first paint.
        estimateSize: (index) => (rowHasDealBadges(data?.[index]) ? 88 : 66),
        overscan: 5,
        measureElement: (el) => el?.getBoundingClientRect().height || 66
    });
    // Force re-measure when:
    //  - expansion state changes (expanded rows are materially taller)
    //  - the data set itself swaps in (new page / new filter / async load),
    //    since async-arrived row data (triage, flags, address line 2) can
    //    grow rows past the initial estimate after virtualizer mount.
    useEffect(() => { rowVirtualizer.measure(); }, [expandedRows]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { rowVirtualizer.measure(); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
    // On page change, reset the scroll container to the top so the new page
    // starts at row 1 rather than wherever the previous page was scrolled.
    useEffect(() => {
        scrollParentRef.current?.scrollTo({ top: 0 });
        setDealStatusDropdownLead(null);
        setOutreachDropdownLead(null);
    }, [page]);

    // Inline editing state
    const [editingCell, setEditingCell] = useState(null); // { leadId, field }
    const [editedValues, setEditedValues] = useState({}); // { [leadId-field]: value }
    const [savingCell, setSavingCell] = useState(null); // { leadId, field }
    const currentInputValuesRef = useRef({});
    const baselineValuesRef = useRef({});
    const blurTimeoutRef = useRef(null);

    // Toggle row expansion for duplicate entries
    const toggleRowExpansion = (leadId, e) => {
        e.stopPropagation();
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(leadId)) {
                newSet.delete(leadId);
            } else {
                newSet.add(leadId);
            }
            return newSet;
        });
    };

    // ==================== Inline Editing Functions ====================

    // Get unique key for a cell
    const getCellKey = (leadId, field) => `${leadId}-${field}`;

    // Check if a cell is currently being edited
    const isEditing = (leadId, field) => {
        return editingCell && editingCell.leadId === leadId && editingCell.field === field;
    };

    // Check if a cell is currently being saved
    const isSaving = (leadId, field) => {
        return savingCell && savingCell.leadId === leadId && savingCell.field === field;
    };

    // Parse input value based on field type
    const parseValue = (value, field) => {
        const config = EDITABLE_FIELDS[field];
        if (!config) return null;

        const cleaned = value.toString().replace(/[$,]/g, '').trim();
        if (cleaned === '') return null;

        // Handle URL type
        if (config.type === 'url') {
            try {
                new URL(cleaned);
                return cleaned;
            } catch {
                return null;
            }
        }

        if (config.type === 'integer') {
            const parsed = parseInt(cleaned, 10);
            return isNaN(parsed) ? null : parsed;
        }
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? null : parsed;
    };

    // Validate input value
    const isValidInput = (value, field) => {
        if (value === '' || value === null || value === undefined) return true;

        const config = EDITABLE_FIELDS[field];
        if (!config) return false;

        const cleaned = value.toString().replace(/[$,]/g, '').trim();
        if (cleaned === '') return true;

        // Handle URL validation
        if (config.type === 'url') {
            try {
                new URL(cleaned);
                return true;
            } catch {
                return false;
            }
        }

        // Check if it's a valid number
        const numberRegex = config.type === 'integer' ? /^-?\d+$/ : /^-?\d*\.?\d*$/;
        if (!numberRegex.test(cleaned)) return false;

        const parsed = parseValue(value, field);
        if (parsed === null) return false;
        if (config.min !== undefined && parsed < config.min) return false;

        return true;
    };

    // Get raw value from item for a field
    const getRawValue = (item, field) => {
        switch (field) {
            case 'beds':
                return item.verifiedCharacteristics?.bedrooms;
            case 'baths':
                return item.verifiedCharacteristics?.bathrooms;
            case 'sqft':
                return item.verifiedCharacteristics?.livingSquareFeet;
            case 'image':
                // Return first available image URL from any source
                const imgUrls = item.imageUrls || [];
                const imgFolders = item.imageFolders || {};
                return (imgFolders.drive?.[0] || imgFolders.dropbox?.[0] || imgFolders.oneDrive?.[0] || imgUrls[0]) || null;
            default:
                return null;
        }
    };

    // Get display value (edited or original)
    const getDisplayValue = (leadId, field, originalValue) => {
        const key = getCellKey(leadId, field);
        const edited = editedValues[key];
        if (edited !== undefined && edited !== null) {
            return edited;
        }
        return originalValue !== null && originalValue !== undefined ? originalValue.toString() : '';
    };

    // Handle cell click to enter edit mode
    const handleCellClick = (e, leadId, field, originalValue) => {
        e.stopPropagation();
        if (!companyId) return;

        setEditingCell({ leadId, field });
        const key = getCellKey(leadId, field);

        let baselineValue = originalValue !== null && originalValue !== undefined
            ? originalValue.toString()
            : '';

        // Format currency values with commas
        const config = EDITABLE_FIELDS[field];
        if (config?.type === 'currency' && baselineValue !== '') {
            const numValue = parseFloat(baselineValue.replace(/,/g, ''));
            if (!isNaN(numValue)) {
                baselineValue = numberFormatter.format(numValue);
            }
        }

        baselineValuesRef.current[key] = baselineValue;
        currentInputValuesRef.current[key] = baselineValue;
        setEditedValues(prev => ({
            ...prev,
            [key]: baselineValue
        }));
    };

    // Handle input change
    const handleCellChange = (leadId, field, value) => {
        const key = getCellKey(leadId, field);

        // Format currency values with commas as user types
        const config = EDITABLE_FIELDS[field];
        let formattedValue = value;
        if (config?.type === 'currency') {
            const cleaned = value.replace(/,/g, '');
            if (cleaned !== '' && /^\d+$/.test(cleaned)) {
                formattedValue = numberFormatter.format(parseInt(cleaned, 10));
            } else {
                formattedValue = cleaned;
            }
        }

        currentInputValuesRef.current[key] = formattedValue;
        setEditedValues(prev => ({
            ...prev,
            [key]: formattedValue
        }));
    };

    // Handle blur (autosave on blur)
    const handleCellBlur = (leadId, field, rawValue) => {
        // Small timeout to allow for any click events to process first
        blurTimeoutRef.current = setTimeout(() => {
            handleCellSave(leadId, field, rawValue);
            blurTimeoutRef.current = null;
        }, 100);
    };

    // Handle save
    const handleCellSave = async (leadId, field, originalValue) => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }

        const key = getCellKey(leadId, field);
        const editedValue = currentInputValuesRef.current[key] ?? editedValues[key];

        if (editedValue === undefined || editedValue === null) {
            setEditingCell(null);
            return;
        }

        const parsed = parseValue(editedValue, field);
        if (parsed === null && editedValue !== '') {
            // Invalid value, revert to baseline and close
            const baselineValue = baselineValuesRef.current[key];
            if (baselineValue !== undefined) {
                setEditedValues(prev => ({
                    ...prev,
                    [key]: baselineValue
                }));
            }
            setEditingCell(null);
            return;
        }

        const newValue = parsed;
        const originalParsed = originalValue !== null && originalValue !== undefined ? originalValue : null;

        // Only save if value changed
        if (newValue === originalParsed || (newValue === null && originalParsed === null)) {
            setEditingCell(null);
            setEditedValues(prev => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });
            delete baselineValuesRef.current[key];
            delete currentInputValuesRef.current[key];
            return;
        }

        // Update display immediately
        setEditedValues(prev => ({
            ...prev,
            [key]: newValue !== null ? newValue.toString() : ''
        }));
        currentInputValuesRef.current[key] = newValue !== null ? newValue.toString() : '';
        setEditingCell(null);
        setSavingCell({ leadId, field });

        try {
            const fieldConfig = EDITABLE_FIELDS[field];
            // For URL fields (image), save as array
            const valueToSave = fieldConfig.type === 'url' ? [newValue] : newValue;

            const updateData = {
                [`data.${fieldConfig.path}`]: valueToSave,
                'data.updatedAt': new Date()
            };

            // Route through the batched mutation queue. Multiple cell edits
            // in the same 500ms window collapse into one Firestore writeBatch.
            // Fire-and-forget; on commit failure the leads cache invalidates
            // and the optimistic value reconciles with the server.
            enqueueLeadEdit(companyId, leadId, updateData, queryClient);

            // Update parent state (optimistic — kept here so the rest of the
            // page sees the change without waiting for the queue flush).
            if (onLeadUpdate) {
                onLeadUpdate(leadId, field, newValue);
            }

            // Clear state
            delete baselineValuesRef.current[key];
            setSavingCell(null);
        } catch (error) {
            console.error('Error saving lead field:', error);
            // Revert on error
            const baselineValue = baselineValuesRef.current[key];
            if (baselineValue !== undefined) {
                currentInputValuesRef.current[key] = baselineValue;
                setEditedValues(prev => ({
                    ...prev,
                    [key]: baselineValue
                }));
            } else {
                setEditedValues(prev => {
                    const updated = { ...prev };
                    delete updated[key];
                    return updated;
                });
            }
            setSavingCell(null);
        }
    };

    // Render editable cell
    const renderEditableCell = (item, field, displayValue, rawValue) => {
        const leadId = item.leadId;
        const isEditingThis = isEditing(leadId, field);
        const isSavingThis = isSaving(leadId, field);
        const key = getCellKey(leadId, field);
        const config = EDITABLE_FIELDS[field];

        if (isEditingThis) {
            const currentValue = editedValues[key] ?? (rawValue !== null && rawValue !== undefined ? rawValue.toString() : '');

            return (
                <div className="leads-edit-container" onClick={(e) => e.stopPropagation()}>
                    <Input
                        key={`input-${key}`}
                        label={null}
                        labelType={null}
                        value={currentValue}
                        onChange={(e) => handleCellChange(leadId, field, e.target.value)}
                        onBlur={() => handleCellBlur(leadId, field, rawValue)}
                        placeholder={config.type === 'url' ? 'https://...' : ''}
                        type="text"
                        leftUnit={config.type === 'currency' ? '$' : null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={isSavingThis}
                        multiline={false}
                        error={!isValidInput(currentValue, field)}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={true}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            );
        }

        // Display mode - show clickable value
        const displayVal = getDisplayValue(leadId, field, rawValue);

        // For URL fields with no value, show "Add link" prompt
        if (config.type === 'url' && !displayVal) {
            return (
                <span
                    className={`leads-editable-cell leads-add-link ${companyId ? 'leads-editable-cell-clickable' : ''}`}
                    onClick={(e) => companyId && handleCellClick(e, leadId, field, rawValue)}
                    title={companyId ? 'Click to add link' : undefined}
                >
                    Add link
                </span>
            );
        }

        const formattedValue = config.type === 'currency'
            ? formatCurrency(parseValue(displayVal, field))
            : (field === 'sqft' ? formatNumber(parseValue(displayVal, field)) : displayVal);

        return (
            <span
                className={`leads-editable-cell ${companyId ? 'leads-editable-cell-clickable' : ''}`}
                onClick={(e) => companyId && handleCellClick(e, leadId, field, rawValue)}
                title={companyId ? 'Click to edit' : undefined}
            >
                {formattedValue || '-'}
            </span>
        );
    };

    // ==================== Deal Status Functions ====================

    const handleDealStatusChange = async (leadId, newStatus) => {
        if (!companyId || !leadId) return;
        const item = data.find(d => d.leadId === leadId);
        const previousStatus = item?.dealStatus || null;
        if (newStatus === previousStatus) return;
        setDealStatusDropdownLead(null);
        setOutreachDropdownLead(null);
        if (onLeadUpdate) {
            onLeadUpdate(leadId, 'dealStatus', newStatus);
        }

        // Build system comment recording the status change
        const currentUser = companyData?.users?.find(u => u.userId === currentUserId);
        const authorName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'Unknown' : 'Unknown';
        const oldLabel = previousStatus ? (dealStatusConfig[previousStatus]?.label || previousStatus) : 'No status';
        const newLabel = newStatus ? (dealStatusConfig[newStatus]?.label || newStatus) : 'No status';
        const statusComment = {
            id: crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            type: 'status_change',
            text: `changed status from ${oldLabel} to ${newLabel}`,
            authorId: currentUserId,
            authorName,
            createdAt: new Date().toISOString()
        };

        if (onLeadUpdate) {
            const currentComments = item?.acquisitionComments || [];
            onLeadUpdate(leadId, 'acquisitionComments', [...currentComments, statusComment]);
        }

        try {
            const db = getFirestore();
            const leadRef = doc(db, 'companies', companyId, 'leads', leadId);
            await updateDoc(leadRef, {
                'data.dealStatus': newStatus,
                'data.acquisitionComments': arrayUnion(statusComment),
                'data.updatedAt': new Date()
            });
            // Fire-and-forget notification email if the new status has notifyEmails configured
            const statusNotifyEmails = dealStatusConfig[newStatus]?.notifyEmails;
            if (statusNotifyEmails?.length > 0) {
                const notifyUrl = process.env.REACT_APP_NOTIFY_DEAL_ESCALATION;
                if (notifyUrl) {
                    cloudFunctionV2(notifyUrl, {
                        companyId, leadId, propertyId: item?.propertyId || leadId,
                        statusKey: newStatus,
                        statusLabel: dealStatusConfig[newStatus]?.label || newStatus
                    }).catch(err => console.error('Deal status notification failed:', err));
                }
            }
        } catch (err) {
            console.error('Error saving deal status:', err);
            // Revert optimistic update on failure
            if (onLeadUpdate) {
                onLeadUpdate(leadId, 'dealStatus', previousStatus);
                const revertedComments = (item?.acquisitionComments || []);
                onLeadUpdate(leadId, 'acquisitionComments', revertedComments);
            }
        }
    };

    // ==================== Outreach Status ====================

    const handleOutreachStatusChange = (leadId, newStatus) => {
        if (!companyId || !leadId) return;
        const item = data.find(d => d.leadId === leadId);
        const previousStatus = item?.leadReviewStatus || 'new';
        if (newStatus === previousStatus) {
            setOutreachDropdownLead(null);
            return;
        }
        setOutreachDropdownLead(null);
        if (onLeadUpdate) onLeadUpdate(leadId, 'leadReviewStatus', newStatus);
        enqueueLeadEdit(companyId, leadId, {
            'data.leadReviewStatus': newStatus,
            'data.updatedAt': new Date()
        }, queryClient);
    };

    // ==================== End Inline Editing Functions ====================

    // Sensors for drag-and-drop with activation constraint to prevent accidental drags
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 }, // Require 5px movement before drag starts
        }),
        useSensor(KeyboardSensor)
    );

    // Handle drag end
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id && onColumnReorder) {
            onColumnReorder(active.id, over.id);
        }
    };

    // Formatting functions
    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        return formatterLong.format(value).replace('.00', '');
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        return numberFormatter.format(value);
    };

    const formatDateTime = (date) => {
        if (!date) return { display: '-', tooltip: null };

        let dateObj;

        if (date && typeof date.toDate === 'function') {
            dateObj = date.toDate();
        } else if (date && typeof date === 'object' && date._seconds !== undefined) {
            const milliseconds = date._seconds * 1000 + (date._nanoseconds || 0) / 1000000;
            dateObj = new Date(milliseconds);
        } else if (date instanceof Date) {
            dateObj = date;
        } else if (typeof date === 'number') {
            dateObj = new Date(date);
        } else if (typeof date === 'string') {
            dateObj = new Date(date);
        } else {
            return { display: '-', tooltip: null };
        }

        if (isNaN(dateObj.getTime())) {
            return { display: '-', tooltip: null };
        }

        // Exact timestamp for tooltip: "2026-03-02 14:12"
        const pad = (n) => String(n).padStart(2, '0');
        const tooltip = `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())} ${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;

        const now = new Date();
        const diffMs = now - dateObj;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        // < 7 days: relative time
        if (diffMinutes < 1) {
            return { display: 'Just now', tooltip };
        }
        if (diffMinutes < 60) {
            return { display: `${diffMinutes}m ago`, tooltip };
        }
        if (diffHours < 24) {
            return { display: `${diffHours}h ago`, tooltip };
        }
        if (diffDays === 1) {
            return { display: 'Yesterday', tooltip };
        }
        if (diffDays < 7) {
            return { display: `${diffDays}d ago`, tooltip };
        }

        // >= 7 days: absolute date
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const display = dateObj.getFullYear() === now.getFullYear()
            ? `${months[dateObj.getMonth()]} ${dateObj.getDate()}`
            : `${months[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;

        return { display, tooltip };
    };

    const formatAddress = (address) => {
        if (!address) return { line1: '-', line2: null };

        if (address.streetAddress) {
            const line1 = address.streetAddress;
            const line2Parts = [];
            if (address.city) line2Parts.push(address.city);
            if (address.state) line2Parts.push(address.state);
            if (address.zipcode) line2Parts.push(address.zipcode);
            const line2 = line2Parts.length > 0 ? line2Parts.join(', ') : null;
            return { line1, line2 };
        }

        if (address.label) {
            const parts = address.label.split(',').map(p => p.trim());
            if (parts.length >= 2) {
                return {
                    line1: parts[0],
                    line2: parts.slice(1).join(', ')
                };
            }
            return { line1: address.label, line2: null };
        }

        return { line1: '-', line2: null };
    };

    const formatLeadType = (leadType) => {
        if (!leadType) return '-';

        // Map lead types to display names
        const typeMap = {
            'direct_to_seller': 'Direct to Seller',
            'property_lead': 'Property Leads',
            'mls_deal': 'MLS Leads'
        };

        return typeMap[leadType] || leadType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const formatPercentage = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        return `${value.toFixed(1)}%`;
    };

    const formatPropertyType = (propertyType) => {
        if (!propertyType) return '-';
        return propertyType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const formatDistressLabel = (field) => {
        return field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
    };

    const getUserInitials = (userObj) => {
        if (!userObj) return '?';
        const first = userObj.firstName?.[0] || '';
        const last = userObj.lastName?.[0] || '';
        return (first + last).toUpperCase() || '?';
    };

// Format custom field value based on type
    const formatCustomFieldValue = (value, fieldType) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') {
            // Check if it looks like a currency value
            if (fieldType === 'number' && value >= 1000) {
                return formatCurrency(value);
            }
            return formatNumber(value);
        }
        return String(value);
    };

    // Build custom columns from available fields
    const customColumns = availableCustomFields.map(field => ({
        key: `custom_${field.key}`,
        label: field.displayName,
        width: '120px',
        sortable: false,
        isCustom: true,
        fieldKey: field.key,
        fieldType: field.type
    }));

    // All available columns
    const allColumns = [...STANDARD_COLUMNS, ...customColumns];

    // Auto-include columns based on active filters
    const effectiveVisibleColumns = (() => {
        let cols = visibleColumns;

        // MLS deals: add motivation after Status, add MAO columns after links
        if (leadTypeFilter === 'mls_deal' && cols) {
            if (!cols.includes('motivation')) {
                const statusIdx = cols.indexOf('leadReviewStatus');
                cols = [...cols.slice(0, statusIdx + 1), 'motivation', ...cols.slice(statusIdx + 1)];
            }
            if (!cols.includes('mao')) {
                const linksIdx = cols.indexOf('links');
                if (linksIdx > -1) {
                    cols = [...cols.slice(0, linksIdx + 1), 'mao', 'maoVsAsking', ...cols.slice(linksIdx + 1)];
                }
            } else if (!cols.includes('maoVsAsking')) {
                const maoIdx = cols.indexOf('mao');
                cols = [...cols.slice(0, maoIdx + 1), 'maoVsAsking', ...cols.slice(maoIdx + 1)];
            }
        }
        return cols;
    })();

    // Filter to visible columns (default to all standard if not specified)
    const visibleColumnsList = effectiveVisibleColumns
        ? allColumns.filter(col => effectiveVisibleColumns.includes(col.key) || col.key === 'address' || col.key === 'leadReviewStatus')
        : STANDARD_COLUMNS;

    // Apply column ordering if provided
    let displayColumns = columnOrder
        ? columnOrder
            .map(key => visibleColumnsList.find(col => col.key === key))
            .filter(Boolean)
            .concat(visibleColumnsList.filter(col => !columnOrder.includes(col.key))) // Append any new columns not in order
        : visibleColumnsList;

    // Always ensure status column is first (far left)
    const statusCol = displayColumns.find(col => col.key === 'leadReviewStatus');
    if (statusCol) {
        displayColumns = [
            statusCol,
            ...displayColumns.filter(col => col.key !== 'leadReviewStatus')
        ];
    }

    // For direct_to_seller filter, move 'Main contact' right after 'address'
    if (leadTypeFilter === 'direct_to_seller') {
        const mainContactIndex = displayColumns.findIndex(col => col.key === 'Main contact');
        const addressIndex = displayColumns.findIndex(col => col.key === 'address');
        if (mainContactIndex > -1 && addressIndex > -1 && mainContactIndex !== addressIndex + 1) {
            const mainContactCol = displayColumns[mainContactIndex];
            const withoutContact = displayColumns.filter((_, i) => i !== mainContactIndex);
            const newAddressIndex = withoutContact.findIndex(col => col.key === 'address');
            displayColumns = [
                ...withoutContact.slice(0, newAddressIndex + 1),
                mainContactCol,
                ...withoutContact.slice(newAddressIndex + 1)
            ];
            // Re-ensure status column stays first after reordering
            const statusColAfterReorder = displayColumns.find(col => col.key === 'leadReviewStatus');
            if (statusColAfterReorder && displayColumns[0]?.key !== 'leadReviewStatus') {
                displayColumns = [
                    statusColAfterReorder,
                    ...displayColumns.filter(col => col.key !== 'leadReviewStatus')
                ];
            }
        }
    }

    // Build dynamic grid template
    const gridColumns = displayColumns.map(col => col.width).join(' ');

    // Get cell value for a column
    const getCellValue = (item, column) => {
        if (column.isCustom) {
            const value = item.customFields?.[column.fieldKey];
            return formatCustomFieldValue(value, column.fieldType);
        }

        switch (column.key) {
            case 'leadReviewStatus':
                return item.leadReviewStatus || 'new';
            case 'address':
                return formatAddress(item.address);
            case 'links':
                return {
                    zillow: item.sourceUrls?.zillow || null,
                    redfin: item.sourceUrls?.redfin || null,
                    realtor: item.sourceUrls?.realtor || null,
                    dealUrls: item.dealUrls || [],
                    sourceFavicons: item.sourceFavicons || null,
                    crmContactUrl: item.crmContactUrl || null
                };
            case 'image':
                // Return first available shareable link from cloud storage
                // Priority: Google Drive -> Dropbox -> OneDrive -> imageUrls array
                const imageFolders = item.imageFolders || {};
                const imageUrls = item.imageUrls || [];

                return (imageFolders.drive && imageFolders.drive.length > 0 ? imageFolders.drive[0] : null) ||
                       (imageFolders.dropbox && imageFolders.dropbox.length > 0 ? imageFolders.dropbox[0] : null) ||
                       (imageFolders.oneDrive && imageFolders.oneDrive.length > 0 ? imageFolders.oneDrive[0] : null) ||
                       (imageUrls.length > 0 ? imageUrls[0] : null);
            case 'beds':
                const beds = item.verifiedCharacteristics?.bedrooms;
                return beds !== null && beds !== undefined ? beds : '-';
            case 'baths':
                const baths = item.verifiedCharacteristics?.bathrooms;
                return baths !== null && baths !== undefined ? baths : '-';
            case 'sqft':
                return formatNumber(item.verifiedCharacteristics?.livingSquareFeet);
            case 'yearBuilt': {
                const year = item.verifiedCharacteristics?.yearBuilt;
                return year != null && year > 0 ? year : '-';
            }
            case 'askingPrice':
                return formatCurrency(resolveIngestedAskingPrice(item));
            case 'zillowEstimate':
                return formatCurrency(item.estimates?.zillow);
            case 'redfinEstimate':
                return formatCurrency(item.estimates?.redfin);
            case 'realtorEstimate':
                return formatCurrency(item.estimates?.realtor);
            case 'spread': {
                const estArr = [item.estimates?.zillow, item.estimates?.redfin, item.estimates?.realtor].filter(v => v != null && !isNaN(v));
                const askPrice = item.currentAskingPrice;
                // Compute median for the tooltip regardless of source
                let median = null;
                if (estArr.length > 0) {
                    estArr.sort((a, b) => a - b);
                    const mid = Math.floor(estArr.length / 2);
                    median = estArr.length % 2 === 0 ? (estArr[mid - 1] + estArr[mid]) / 2 : estArr[mid];
                }
                // Prefer server-persisted value, fall back to client computation
                if (item.computedSpread != null) {
                    return { value: item.computedSpread, median, askingPrice: askPrice };
                }
                if (estArr.length === 0 || !askPrice) return null;
                const spread = Math.round(((median - askPrice) / askPrice) * 1000) / 10;
                return { value: spread, median, askingPrice: askPrice };
            }
            // Underwriting columns
            case 'arv':
                return formatCurrency(item.underwriting?.arv);
            case 'repairCost':
                return formatCurrency(item.underwriting?.repairCost);
            case 'profit':
                return formatCurrency(item.underwriting?.estimatedProfit);
            case 'profitPct':
                return formatPercentage(item.underwriting?.estimatedProfitPercentage);
            case 'roi':
                return formatPercentage(item.underwriting?.roiPercent);
            case 'mao':
                return formatCurrency(item.underwriting?.maxAllowableOffer);
            case 'maoVsAsking': {
                // OUR price and the SELLER's ask — the pair the server computes computedMaoVsAsking
                // from. This used to pair the gap with the FLIP MAO and the calculator-editable
                // currentAskingPrice, so the tooltip named a price the percentage was not about.
                const { price: mao, basis } = ourPriceOf(item);
                const askPriceMao = sellerAskOf(item);
                // Always fresh, a dash when it cannot be — see gapForDisplay: the stored value is never
                // shown (stale whenever the inputs are missing, the FLIP gap on a lead nobody re-synced).
                const value = gapForDisplay(item);
                if (value == null) return null;
                // The fee that came off (a co-living basis only), so the tooltip can say so: our price
                // sits $2,500 under the gross MAOs and an unexplained gap reads as a bug.
                const lessFee = basis === 'buyer' ? buyerFeeOf(item) : basis && basis !== 'flip' ? (positiveOrNull(item.coLivingWholesaleFee) ?? 0) : 0;
                return { value, mao, basis, lessFee, askingPrice: askPriceMao };
            }
            case 'dealScore': {
                const v = item.coLivingDealScore;
                if (v == null) return null;
                return { value: v, grade: item.coLivingCashFlowGrade ?? null, ncf: item.coLivingNcfAtMao ?? null, mao: item.coLivingMao ?? null };
            }
            case 'maoVsAsk': {
                // Read the persisted scalar rather than recomputing mao ÷ ask here: it is the field
                // the server sorted the page on, and deriving a second copy off `currentAskingPrice`
                // would let the printed number disagree with the row order the moment a price cut
                // lands ahead of the rank recompute.
                const v = item.coLivingMaoPctOfAsk;
                if (v == null) return null;
                return { value: v, mao: item.coLivingMao ?? null, askingPrice: item.currentAskingPrice ?? null };
            }
            case 'report':
                return {
                    countyId: item.countyId,
                    propertyId: item.propertyId,
                    hasUnderwriting: !!(item.underwriting?.arv || item.underwriting?.repairCost)
                };
            case 'distressSignals': {
                const ds = item.distressSignals;
                if (!ds?.active?.length) return null;
                const sorted = [...ds.active].sort((a, b) => {
                    const ai = DISTRESS_PRIORITY[a] ?? Number.MAX_SAFE_INTEGER;
                    const bi = DISTRESS_PRIORITY[b] ?? Number.MAX_SAFE_INTEGER;
                    return ai - bi;
                });
                const labels = sorted.map(formatDistressLabel);
                return {
                    visible: labels.slice(0, DISTRESS_VISIBLE_LIMIT),
                    overflow: labels.slice(DISTRESS_VISIBLE_LIMIT),
                    all: labels,
                };
            }
            case 'motivation': {
                const topSignals = item.motivationTopSignals;
                const tier = item.motivationTier;
                if (!topSignals?.length && !tier) return null;
                const signals = topSignals || [];
                return {
                    tier,
                    visible: signals.slice(0, MOTIVATION_VISIBLE_LIMIT),
                    overflow: signals.slice(MOTIVATION_VISIBLE_LIMIT),
                    all: signals,
                };
            }
            case 'notes': {
                const notes = item.notes;
                if (!notes) return '-';
                return notes;
            }
            case 'acquisitionNotes': {
                const comments = item.acquisitionComments;
                if (comments && comments.length > 0) {
                    return comments[comments.length - 1].text;
                }
                // Fallback to legacy string
                const acqNotes = item.acquisitionNotes;
                if (!acqNotes) return '-';
                return acqNotes;
            }
            case 'dealStatus':
                return item.dealStatus || null;
            case 'portfolio':
                return item.portfolioId
                    ? { portfolioId: item.portfolioId, name: item.portfolioName }
                    : null;
            case 'propertyType':
                return formatPropertyType(item.propertyType);
            case 'leadType':
                return formatLeadType(item.leadType);
            case 'assigned': {
                const normalized = normalizeAssignmentData(item);
                const roles = normalized.assignedRoles;
                const assignees = ['senior', 'middle', 'junior']
                    .map(role => {
                        const u = roles[role];
                        if (!u?.userId) return null;
                        const rosterUser = companyData?.users?.find(cu => cu.userId === u.userId);
                        return { role, ...(rosterUser || u) };
                    })
                    .filter(Boolean);
                return assignees.length > 0 ? assignees : null;
            }
            case 'source':
                return item.source || item.sourceMetadata?.rawSource || null;
            case 'Main contact': {
                const mainSender = pickMainEntry(item.entries)?.sender || item.originalSender || item.currentSender;
                return getSenderDisplay(mainSender, '-');
            }
            case 'mainContactEmail': {
                const mainSender = pickMainEntry(item.entries)?.sender || item.originalSender || item.currentSender;
                return mainSender?.email || '-';
            }
            case 'mainContactPhone': {
                const mainSender = pickMainEntry(item.entries)?.sender || item.originalSender || item.currentSender;
                return mainSender?.phone || '-';
            }
            case 'createdAt':
                return item.createdAt || null;
            case 'updatedAt':
                return item.updatedAt || null;
            default:
                return '-';
        }
    };

    // Render cell content
    const renderCell = (item, column) => {
        const value = getCellValue(item, column);

        if (column.key === 'leadReviewStatus') {
            // If deal status is unqualified, override the review status badge (not editable)
            const dealStatus = item.dealStatus || '';
            if (dealStatus.startsWith('unqualified_')) {
                return (
                    <Badge
                        statusDot={true}
                        bgColour="bg-colour-red-01"
                        text="Unqualified"
                        textClass="label-bold colour-primary"
                        statusDotColour={colour.red02}
                        icon={null}
                        tooltip={false}
                        tooltipBody={null}
                        tooltipPosition="top"
                        iconLeft={false}
                    />
                );
            }
            const outreachStatusConfig = {
                new:    { text: 'New',    bg: 'bg-colour-blue-01',  dot: colour.blue02 },
                viewed: { text: 'Viewed', bg: 'bg-colour-yellow-01', dot: colour.yellow02 },
                sent:   { text: 'Sent',   bg: 'bg-colour-green-01', dot: colour.jade02 },
            };
            const config = outreachStatusConfig[value] || outreachStatusConfig.new;
            // "No photos": nothing we can show the operator — no MLS/portal
            // photos AND no Drive/Dropbox/OneDrive folder pulled. Soft red. It
            // overrides the outreach status display (New/Viewed/Sent); the dropdown
            // remains editable underneath. Unqualified takes precedence (above).
            //
            // ⚠️ THIS MUST MEAN "WE LOOKED AND THERE ARE NONE", never "we haven't
            // answered yet". The old test was `(realPhotoCount || 0) === 0`, which
            // was wrong in both directions:
            //
            //   1. `photoAvailability` is written ONCE COLLECTION SETTLES, by
            //      imagePipeline.js and downloadLeadImages.js, and both writes are
            //      best-effort (they log and swallow). So `undefined` means "not
            //      answered" — and `|| 0` read it as a confident zero.
            //   2. An MLS lead's gallery comes off the portal. Those photos are
            //      renderable and need no Drive/Dropbox link at all, so a count
            //      derived from downloaded assets says nothing about whether the
            //      operator can see the house. Badging them was the visible bug.
            //
            // So: ask what we can actually RENDER first (deriveDisplayImageUrls is
            // the same resolver the address thumbnail and the deal page use, so the
            // badge can no longer disagree with the picture next to it), discount
            // street-view/satellite stubs — a lead whose only images are map assets
            // genuinely has no photos — and only then fall back to the settled count.
            const displayImageUrls = deriveDisplayImageUrls(item, null);
            const hasRenderablePhotos = displayImageUrls.some(url => !isMapOrStreetViewImage(url));
            const noPhotos = !hasRenderablePhotos
                && !arePhotosPending(item, displayImageUrls)
                && (item.photoAvailability?.realPhotoCount || 0) === 0;
            const triggerConfig = noPhotos
                ? { text: 'No photos', bg: 'bg-colour-red-01', dot: colour.red02 }
                : config;
            const isOutreachOpen = outreachDropdownLead === item.leadId;
            const outreachBadge = (cfg) => (
                <Badge
                    statusDot={true}
                    bgColour={cfg.bg}
                    text={cfg.text}
                    textClass="label-bold colour-primary"
                    statusDotColour={cfg.dot}
                    icon={null}
                    tooltip={false}
                    tooltipBody={null}
                    tooltipPosition="top"
                    iconLeft={false}
                />
            );
            return (
                <StatusBadgeDropdown
                    isOpen={isOutreachOpen}
                    onToggle={() => {
                        if (onActionsOpenChange) onActionsOpenChange(null);
                        setDealStatusDropdownLead(null);
                        setOutreachDropdownLead(isOutreachOpen ? null : item.leadId);
                    }}
                    onClose={() => setOutreachDropdownLead(null)}
                    trigger={outreachBadge(triggerConfig)}
                    options={['new', 'viewed', 'sent'].map((key) => ({
                        key,
                        value: key,
                        selected: key === value,
                        content: outreachBadge(outreachStatusConfig[key]),
                    }))}
                    onSelect={(key) => handleOutreachStatusChange(item.leadId, key)}
                />
            );
        }

        if (column.key === 'source') {
            // Use the rich profile resolver (TLD-aware for Gmail/Outlook, CRM-
            // aware via crmContactUrl + writeBack flags). Channel-based bg
            // stays sourced from `item.source` so Email-class profiles
            // (gmail/outlook/email-generic) all share the same blue.
            const profile = resolveLeadSourceProfile({ lead: { data: item } });
            const bg = getLeadSourceBgClass(value);
            // Surface the specific inbox the email arrived at when we know it
            // — a company can have N Gmail accounts connected via Unipile, so
            // "Gmail" alone isn't enough to tell which mailbox received the
            // lead. Tooltip is the only thing that fits in a table cell.
            // The Badge component renders `text` as the tooltip title, so the
            // body skips the redundant provider name and just names the inbox.
            const toEmail = item.sourceMetadata?.toEmail;
            const tooltipBody = toEmail ? `Received at ${toEmail}` : null;
            return (
                <Badge
                    statusDot={false}
                    bgColour={bg}
                    text={profile.label}
                    textClass="label-bold colour-primary"
                    statusDotColour={null}
                    icon={<SourceIcon profile={profile} size={12} />}
                    tooltip={!!tooltipBody}
                    tooltipBody={tooltipBody}
                    tooltipPosition="top"
                    iconLeft={true}
                />
            );
        }

        if (column.key === 'dealStatus') {
            const statusVal = value;
            const config = statusVal ? dealStatusConfig[statusVal] : null;
            const isOpen = dealStatusDropdownLead === item.leadId;

            return (
                <StatusBadgeDropdown
                    isOpen={isOpen}
                    onToggle={() => {
                        // Close kebab menu and outreach dropdown if open
                        if (onActionsOpenChange) onActionsOpenChange(null);
                        setOutreachDropdownLead(null);
                        setDealStatusDropdownLead(isOpen ? null : item.leadId);
                    }}
                    onClose={() => setDealStatusDropdownLead(null)}
                    trigger={config ? (
                        <Badge
                            statusDot={true}
                            bgColour={config.bg}
                            text={config.label}
                            textClass="label-bold colour-primary"
                            statusDotColour={config.dot}
                            icon={null}
                            tooltip={false}
                            tooltipBody={null}
                            tooltipPosition="top"
                            iconLeft={false}
                        />
                    ) : (
                        <span className="label-regular colour-tertiary">No status</span>
                    )}
                    options={dealStatusOptions.map((opt) => ({
                        key: opt.value ?? 'null',
                        value: opt.value,
                        selected: opt.value === statusVal,
                        content: opt.value && dealStatusConfig[opt.value] ? (
                            <Badge
                                statusDot={true}
                                bgColour={dealStatusConfig[opt.value].bg}
                                text={opt.label}
                                textClass="label-bold colour-primary"
                                statusDotColour={dealStatusConfig[opt.value].dot}
                                icon={null}
                                tooltip={false}
                                tooltipBody={null}
                                tooltipPosition="top"
                                iconLeft={false}
                            />
                        ) : (
                            <span className="colour-tertiary">{opt.label}</span>
                        ),
                    }))}
                    onSelect={(val) => handleDealStatusChange(item.leadId, val)}
                />
            );
        }

        // ARV / Repair Cost cells: show the dollar value when present and the
        // CalcStatus tri-state icon (green check / yellow estimated / red
        // skipped) next to it. When there's no value, render the icon alone —
        // the hover tooltip explains why (e.g. "no images", "out of coverage").
        // Wrap item in {data: item} because ArvCell/RepairsCell expect the
        // {leadId, data} shape used elsewhere.
        if (column.key === 'arv') {
            const arvValue = item.underwriting?.arv;
            const hasValue = arvValue != null && !isNaN(arvValue) && arvValue > 0;
            return (
                <div className="leads-calc-cell">
                    {hasValue && <span className="body-regular colour-primary">{formatCurrency(arvValue)}</span>}
                    <ArvCell lead={{ data: item }} />
                </div>
            );
        }
        if (column.key === 'repairCost') {
            const rcValue = item.underwriting?.repairCost;
            const hasValue = rcValue != null && !isNaN(rcValue) && rcValue > 0;
            return (
                <div className="leads-calc-cell">
                    {hasValue && <span className="body-regular colour-primary">{formatCurrency(rcValue)}</span>}
                    <RepairsCell lead={{ data: item }} />
                </div>
            );
        }

        if (column.key === 'address') {
            const entryCount = item.entries?.length || 1;
            const hasMultipleEntries = entryCount > 1;
            const isExpanded = expandedRows.has(item.leadId);
            // The strategy-fit badges (Fix & flip / Co-living / Long-term rental) and the
            // floor-plan badge were removed from this row on 2026-08-11 (Liam) — three or four
            // chips per lead crowded the address out of a fixed-width column, and the verdicts
            // they carried are on the lead detail page. The buyer count is the only one left.
            // ⚠️ This was `src/utils/strategyBadge.js`'s ONLY caller — the module and its unit
            // test are now unreferenced by the app. Left in place deliberately rather than
            // deleted alongside a UI tweak; it is pure and tested if the badges ever come back.
            const buyerMatchCount = matchedBuyerCount(item);
            // Show the wholesale chip when the deal actually matched roster buyers.
            // Deliberately NOT keyed off data.dealMode (retired — it was a stale
            // MCP-only flag that could hide real buyer demand); a self-only company
            // has no roster to match against, so this self-selects.
            const showsBuyerChip = buyerMatchCount > 0;
            // Needs-attention surface: prefer the denormalized flag set at
            // ingestion time; fall back to live computation for legacy leads
            // that pre-date the flag write (see functions/.../leadFlags.js).
            // Pending is resolved from the underwriting OUTCOME, not from item.status: a
            // property emailed in twice keeps status:'processing' forever (the resubmission
            // merge path never lands a terminal status), which pinned these cells on
            // "Underwriting in progress" over real numbers. See isUnderwritingPending.
            const itemPending = isUnderwritingPending(item);
            const liveArv = resolveArvStatus(item, itemPending);
            const liveRepairs = resolveRepairsStatus(item, itemPending);
            const liveNeedsAttention =
                liveArv.state === CALC_STATES.SKIPPED ||
                liveArv.state === CALC_STATES.ESTIMATED ||
                liveRepairs.state === CALC_STATES.SKIPPED ||
                liveRepairs.state === CALC_STATES.ESTIMATED;
            const needsAttention = item.flags?.needsAttention === true || liveNeedsAttention;

            return (
                <div className="leads-address-container">
                    <div className="leads-address-line-one">
                        {needsAttention && (
                            <span
                                className="leads-attention-dot"
                                title="Underwriting needs attention — hover the ARV / Repair Cost cells for details"
                                aria-label="Needs attention"
                            />
                        )}
                        <span className="body-semibold colour-primary leads-address-text">{value.line1}</span>
                        {hasMultipleEntries && (
                            <span
                                className={`leads-entries-badge ${isExpanded ? 'leads-entries-badge-active' : ''}`}
                                onClick={(e) => toggleRowExpansion(item.leadId, e)}
                                title={`${entryCount} submissions - click to ${isExpanded ? 'collapse' : 'expand'}`}
                            >
                                {entryCount}
                            </span>
                        )}
                        {item.portfolioId && (() => {
                            const pColour = getPortfolioColour(item.portfolioId);
                            return (
                                <span
                                    className={`leads-portfolio-badge ${pColour.bg} ${pColour.text}`}
                                    title={`Portfolio: ${item.portfolioName || 'Unnamed'}`}
                                >
                                    {item.portfolioName || 'Portfolio'}
                                </span>
                            );
                        })()}
                        {item.isArchived === true && (
                            <span
                                className="leads-archived-badge label-bold-caps colour-secondary"
                                title="This lead is archived"
                            >
                                Archived
                            </span>
                        )}
                        {item.outsideBuyBox === true && (() => {
                            // `geography_unknown` on its own means we never placed the property,
                            // so "out of buy box" would assert something we never established.
                            // Only when it is the SOLE reason — beside price/characteristics the
                            // lead really is out of box on those.
                            const obReasons = Array.isArray(item.outsideBuyBoxReasons) ? item.outsideBuyBoxReasons : [];
                            const unlocatable = obReasons.length === 1 && obReasons[0] === 'geography_unknown';
                            return (
                                <span
                                    className="leads-outside-buybox-badge label-bold-caps"
                                    title={unlocatable
                                        ? "You submitted this deal manually. We couldn't work out exactly where it is, so the buy-box area check never ran — this is not a finding that it's outside your area."
                                        : "You submitted this deal manually — it's outside your buy box, but we analyzed it anyway."}
                                >
                                    {unlocatable ? 'Location unknown' : 'Out of buy box'}
                                </span>
                            );
                        })()}
                        {!item.propertyId && onEditAddress && (
                            <span
                                className="leads-edit-address-link label-bold colour-link cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEditAddress(item.leadId, item.address);
                                }}
                                title="Edit address and re-run enrichment"
                            >
                                Edit
                            </span>
                        )}
                    </div>
                    {value.line2 && <span className="body-regular colour-secondary">{value.line2}</span>}
                    {/* Buyer demand on its own wrapping row so it never squeezes the street
                        address (the column track is a fixed width). */}
                    {showsBuyerChip && (
                        <div className="leads-deal-badges">
                            <span
                                className="leads-buyers-badge xs-label-bold"
                                title={`${buyerMatchCount} buyer${buyerMatchCount === 1 ? '' : 's'} on your roster match this deal`}
                            >
                                {`${buyerMatchCount} buyer${buyerMatchCount === 1 ? '' : 's'}`}
                            </span>
                        </div>
                    )}
                </div>
            );
        }

        if (column.key === 'links') {
            const urls = value;
            const hasAnyUrl = urls.zillow || urls.redfin || urls.realtor || (urls.dealUrls && urls.dealUrls.length > 0) || urls.crmContactUrl;
            if (!hasAnyUrl) return '-';

            return (
                <div className="leads-links-container">
                    {urls.zillow && (
                        <div
                            className="leads-link-icon bg-colour-white"
                            onClick={(e) => { e.stopPropagation(); window.open(urls.zillow, '_blank'); }}
                            title="View on Zillow"
                        >
                            <Zillow />
                        </div>
                    )}
                    {urls.redfin && (
                        <div
                            className="leads-link-icon bg-colour-white"
                            onClick={(e) => { e.stopPropagation(); window.open(urls.redfin, '_blank'); }}
                            title="View on Redfin"
                        >
                            <Redfin />
                        </div>
                    )}
                    {urls.realtor && (
                        <div
                            className="leads-link-icon bg-colour-white"
                            onClick={(e) => { e.stopPropagation(); window.open(urls.realtor, '_blank'); }}
                            title="View on Realtor.com"
                        >
                            <Realtor />
                        </div>
                    )}
                    {urls.dealUrls.filter(Boolean).map((dealUrl, index) => {
                        let domain;
                        try { domain = new URL(dealUrl).hostname.replace(/^www\./, ''); }
                        catch { domain = 'external site'; }
                        const faviconSrc = getFaviconSrc(domain, urls.sourceFavicons);
                        const siteName = getSiteName(domain, urls.sourceFavicons);
                        return (
                            <div
                                key={`deal-${index}`}
                                className="leads-link-icon bg-colour-white"
                                onClick={(e) => { e.stopPropagation(); window.open(dealUrl, '_blank'); }}
                                title={`View on ${siteName}`}
                            >
                                <FaviconImg src={faviconSrc} alt={siteName} size={14} />
                            </div>
                        );
                    })}
                    {urls.crmContactUrl && (
                        <div
                            className="leads-link-icon bg-colour-white"
                            onClick={(e) => { e.stopPropagation(); window.open(urls.crmContactUrl.url, '_blank'); }}
                            title={`View in ${urls.crmContactUrl.label}`}
                        >
                            <ExternalLink fill={colour.gray06} />
                        </div>
                    )}
                </div>
            );
        }

        if (column.key === 'image') {
            // View-only folder: photos exist but the sender disabled downloads,
            // so we couldn't pull them. Show whenever the lead lacks a full
            // downloaded gallery (tier none/limited) — a view-only folder almost
            // always coexists with a stray enrichment photo or two, so gate on
            // tier, not realPhotoCount===0. Clickable → opens the folder to VIEW.
            const viewOnlyFolder = (item.photoAvailability?.tier !== 'gallery')
                ? deriveViewOnlyFolder(item.photoAvailability)
                : null;
            if (viewOnlyFolder) {
                return (
                    <span
                        className="triage-red-flag-inline leads-editable-cell-clickable"
                        title="Photos are view-only — the sender disabled downloads. Click to view them in the folder."
                        onClick={(e) => { e.stopPropagation(); window.open(viewOnlyFolder.url, '_blank'); }}
                    >
                        <Warning fill={colour.orange03} />
                        View only
                    </span>
                );
            }
            if (value) {
                const usableDirectUrls = (item.imageUrls || []).filter(u => u && !getImageFolderProvider(u) && !isUnsupportedImageProvider(u));
                const isSupportedProvider = !!getImageFolderProvider(value) || usableDirectUrls.length >= 2;

                if (isSupportedProvider) {
                    // Supported cloud-storage link — normal icon
                    return (
                        <IconButton
                            size="small"
                            icon={<ProviderIcon url={value} fallback={<Image fill={colour.gray06} className="leads-image-icon" />} />}
                            standardIcon={false}
                            disabled={false}
                            clickFunc={(e) => {
                                e.stopPropagation();
                                window.open(value, '_blank');
                            }}
                            contextTooltip={true}
                            contextTooltipBG="dark"
                            contextTooltipText="View property images"
                            background={true}
                            bgType="white"
                            customClass="leads-image-button"
                        />
                    );
                }

                // Unsupported / unrecognised link — inline warning text mirroring LeadDetail
                return (
                    <span
                        className="triage-red-flag-inline leads-editable-cell-clickable"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenImageModal) {
                                onOpenImageModal(item.leadId, value);
                            } else {
                                window.open(value, '_blank');
                            }
                        }}
                    >
                        <Warning fill={colour.orange03} />
                        Not downloadable
                    </span>
                );
            }

            // No image URL - show "Add link" that opens modal
            return (
                <span
                    className={`leads-editable-cell leads-add-link ${onOpenImageModal ? 'leads-editable-cell-clickable' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onOpenImageModal) {
                            onOpenImageModal(item.leadId, null);
                        }
                    }}
                    title={onOpenImageModal ? 'Click to add link' : undefined}
                >
                    Add link
                </span>
            );
        }

        if (column.key === 'report') {
            if (value.countyId && value.propertyId && value.hasUnderwriting) {
                return (
                    <span
                        className="leads-report-link body-regular"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/run-comps/${value.countyId}/${value.propertyId}`);
                        }}
                        title="View underwriting report"
                    >
                        View
                    </span>
                );
            }
            return '-';
        }

        if (column.key === 'portfolio') {
            if (!value) return <span className="colour-secondary body-regular">-</span>;
            const pColour = getPortfolioColour(value.portfolioId);
            return (
                <span
                    className={`leads-portfolio-badge leads-portfolio-badge-cell ${pColour.bg} ${pColour.text}`}
                >
                    {value.name || 'Portfolio'}
                </span>
            );
        }

        if (column.key === 'distressSignals') {
            if (!value) return <span className="body-regular colour-tertiary">-</span>;
            const { visible, overflow, all } = value;
            return (
                <div className="distress-signals-cell">
                    {visible.map((label, i) => (
                        <span key={i} className="distress-signal-chip xs-label colour-secondary">
                            {label}
                        </span>
                    ))}
                    {overflow.length > 0 && (
                        <SignalOverflowChip
                            chipClassName="signal-overflow-chip xs-label colour-secondary"
                            count={overflow.length}
                            items={all}
                            ariaLabel={`${overflow.length} more distress signals: ${overflow.join(', ')}`}
                        />
                    )}
                </div>
            );
        }

        if (column.key === 'motivation') {
            if (!value) return <span className="body-regular colour-tertiary">-</span>;
            // ⚠️ These keys are the LIVE vocabulary from functions/utils/sellerMotivationProducer.js
            // `toMotivationTier` — 'hot' | 'warm' | 'cool' | 'cold' | 'unknown'. They were the V1
            // scorer's ('HIGHLY_MOTIVATED' etc.) until 2026-08-18, and that scorer
            // (functions/utils/sellerMotivation.js) has had zero callers since 2026-03 — so every
            // lookup here missed, every chip fell through to the grey default, and the
            // no-signals branch below rendered "-" on literally every lead. If you change the
            // producer's tier strings, change these in the same commit.
            const tierColour = {
                hot: 'bg-colour-jade-01 colour-green',
                warm: 'bg-colour-yellow-01 colour-yellow-03',
                cool: 'bg-colour-x-light colour-secondary',
                cold: 'bg-colour-x-light colour-tertiary',
            }[value.tier] || 'bg-colour-x-light colour-secondary';
            const { visible, overflow, all } = value;
            // No specific signals — fall back to the tier label alone. 'unknown' is deliberately
            // absent: it means no price history at all, and a chip reading "Cold" would assert an
            // indifferent seller we have no evidence about. Blind renders as "-".
            if (!all.length) {
                const tierText = { hot: 'Hot', warm: 'Warm', cool: 'Cool', cold: 'Cold' }[value.tier];
                if (!tierText) return <span className="body-regular colour-tertiary">-</span>;
                return <span className={`motivation-signal-chip xs-label ${tierColour}`}>{tierText}</span>;
            }
            return (
                <div className="motivation-cell">
                    {visible.map((sig, i) => (
                        <span key={i} className={`motivation-signal-chip xs-label ${tierColour}`}>
                            {sig}
                        </span>
                    ))}
                    {overflow.length > 0 && (
                        <SignalOverflowChip
                            chipClassName="signal-overflow-chip xs-label colour-secondary"
                            count={overflow.length}
                            items={all}
                            ariaLabel={`${overflow.length} more motivation signals: ${overflow.join(', ')}`}
                        />
                    )}
                </div>
            );
        }

        if (column.key === 'notes') {
            return <span className="leads-notes-cell" title={item.notes || ''}>{value}</span>;
        }

        if (column.key === 'acquisitionNotes') {
            const comments = item.acquisitionComments;
            if (comments && comments.length > 0) {
                const latestText = comments[comments.length - 1].text;
                const count = comments.length;
                const displayText = count > 1 ? `(${count}) ${latestText}` : latestText;
                return <span className="leads-notes-cell" title={latestText}>{displayText}</span>;
            }
            return <span className="leads-notes-cell" title={item.acquisitionNotes || ''}>{value}</span>;
        }

        if (column.key === 'Main contact') {
            if (value === '-' && onOpenContactModal) {
                return (
                    <span
                        className="leads-editable-cell leads-add-link leads-editable-cell-clickable"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenContactModal(item.leadId, item.rawData || item.entries?.[0]?.rawInput);
                        }}
                        title="Click to identify contact from raw data"
                    >
                        Unknown
                    </span>
                );
            }
            if (item.senderMissing === true && onOpenContactModal) {
                return (
                    <span
                        className="leads-sender-cell leads-sender-cell-unverified cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenContactModal(item.leadId, item.rawData || item.entries?.[0]?.rawInput);
                        }}
                        title="Forwarded — click to verify sender"
                    >
                        <span className="body-regular colour-primary">{value}</span>
                        <span className="leads-sender-warning-dot" aria-hidden="true" />
                    </span>
                );
            }
            const showLowestPriceBadge = hasLowerPricedSibling(item.entries);
            const mainSender = pickMainEntry(item.entries)?.sender || item.originalSender || item.currentSender;
            const senderLogo = resolveSenderLogo(item, mainSender);
            return (
                <div className="leads-main-contact-cell">
                    <SenderLogo logoUrl={senderLogo} className="leads-sender-logo" />
                    <span className="leads-sender-cell">{value}</span>
                    {showLowestPriceBadge && (
                        <Badge
                            bgColour="bg-colour-jade-01"
                            text="Lowest price"
                            textClass="xs-label-bold colour-jade-03"
                        />
                    )}
                </div>
            );
        }

        if (column.key === 'assigned') {
            if (!value || value.length === 0) {
                return <span className="colour-secondary body-regular">-</span>;
            }
            if (value.length === 1) {
                const u = value[0];
                const initials = getUserInitials(u);
                const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Unknown';
                const isMe = u.userId === currentUserId;
                const roleLabel = roleLabels[u.role] || u.role;
                return (
                    <div className="leads-assigned-cell" title={`${roleLabel}: ${fullName}`}>
                        <div className={`leads-assigned-avatar ${isMe ? 'leads-assigned-avatar-me' : ''}`}>
                            {initials}
                        </div>
                        <span className="body-regular colour-primary leads-assigned-name">
                            {isMe ? 'Me' : fullName}
                        </span>
                    </div>
                );
            }
            // Multiple assignees — overlapping avatars
            const tooltipLines = value.map(u => {
                const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Unknown';
                const roleLabel = roleLabels[u.role] || u.role;
                return `${roleLabel}: ${u.userId === currentUserId ? 'Me' : name}`;
            }).join('\n');
            return (
                <div className="leads-assigned-cell leads-assigned-stacked" title={tooltipLines}>
                    {value.map((u, i) => {
                        const initials = getUserInitials(u);
                        const isMe = u.userId === currentUserId;
                        return (
                            <div
                                key={u.role}
                                className={`leads-assigned-avatar leads-assigned-avatar-stacked ${isMe ? 'leads-assigned-avatar-me' : ''}`}
                                style={{ zIndex: value.length - i }}
                            >
                                {initials}
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (column.key === 'maoVsAsking') {
            if (!value || typeof value.value !== 'number' || !isFinite(value.value)) return '-';
            const colorClass = value.value >= 0 ? 'colour-green' : 'colour-error';
            const maoStr = value.mao != null ? formatterLong.format(value.mao).replace('.00', '') : '-';
            const askingStr = value.askingPrice != null ? formatterLong.format(value.askingPrice).replace('.00', '') : '-';
            // Name WHICH price — a bare "MAO" reads as the flip number, which this column no longer is.
            const priceLabel = OUR_PRICE_BASIS_LABEL[value.basis] || 'MAO';
            const feeStr = value.lessFee > 0 ? ` less our ${formatterLong.format(value.lessFee).replace('.00', '')} fee` : '';
            const tip = `Our price (${priceLabel}${feeStr}): ${maoStr} vs. Asking: ${askingStr}`;
            return (
                <span className={`body-semibold ${colorClass}`} title={tip}>
                    {`${Math.round(Math.abs(value.value))}% ${value.value >= 0 ? 'above' : 'below'} asking`}
                </span>
            );
        }


        if (column.key === 'dealScore') {
            if (!value || typeof value.value !== 'number' || !isFinite(value.value)) return '-';
            const v = value.value;
            // Green from 80, amber from 60, otherwise neutral — a low score is not an error, it is
            // a deal that needs a real discount.
            const colorClass = v >= 80 ? 'colour-green' : v >= 60 ? 'colour-yellow-03' : 'colour-secondary';
            // The grade is WHY the score moved; naming it saves opening the deal to find out.
            const gradeLabel = value.grade === 'below_min' ? 'thin cash flow'
                : value.grade === 'target' ? 'strong cash flow' : null;
            const maoStr = value.mao != null ? formatterLong.format(value.mao).replace('.00', '') : null;
            const ncfStr = value.ncf != null ? formatterLong.format(value.ncf).replace('.00', '') : null;
            const tip = [
                maoStr ? `Pay up to ${maoStr} and you get your cash back out.` : null,
                ncfStr ? `Cash flow at that price: ${ncfStr}/mo.` : null,
            ].filter(Boolean).join(' ');
            return (
                <span className={`body-semibold ${colorClass}`} title={tip || undefined}>
                    {Math.round(v)}{gradeLabel ? ` · ${gradeLabel}` : ''}
                </span>
            );
        }

        if (column.key === 'maoVsAsk') {
            if (!value || typeof value.value !== 'number' || !isFinite(value.value)) return '-';
            const v = value.value;
            // Same bands as Deal Score, on the same 0-100 scale, so the two columns read
            // consistently when both are on. A negative means no price clears the bar at all.
            const colorClass = v >= 80 ? 'colour-green' : v >= 60 ? 'colour-yellow-03' : 'colour-secondary';
            const maoStr = value.mao != null ? formatterLong.format(value.mao).replace('.00', '') : null;
            const askStr = value.askingPrice != null ? formatterLong.format(value.askingPrice).replace('.00', '') : null;
            const gap = Math.round(100 - v);
            // A negative ratio means the costs exceed what the refinance returns — no price
            // works, at any discount. "Needs 112% off" is the arithmetic being read out loud
            // rather than the fact, so say the fact.
            const verdict = v < 0 ? 'No purchase price gets your cash back out here.'
                : gap > 0 ? `Needs ${gap}% off.`
                    : 'Works at full asking price.';
            const tip = [
                maoStr && askStr && v >= 0 ? `Pay up to ${maoStr} against an ask of ${askStr}.` : null,
                verdict,
            ].filter(Boolean).join(' ');
            return (
                <span className={`body-semibold ${colorClass}`} title={tip}>
                    {v.toFixed(1)}%
                </span>
            );
        }

        if (column.key === 'spread') {
            if (!value || typeof value.value !== 'number' || !isFinite(value.value)) return '-';
            const sign = value.value >= 0 ? '+' : '';
            const colorClass = value.value >= 0 ? 'colour-green' : 'colour-red-02';
            const medianStr = value.median != null ? formatterLong.format(value.median).replace('.00', '') : '-';
            const askingStr = value.askingPrice != null ? formatterLong.format(value.askingPrice).replace('.00', '') : '-';
            const tip = `Median estimate: ${medianStr} vs. Asking: ${askingStr}`;
            return (
                <span className={`body-semibold ${colorClass}`} title={tip}>
                    {sign}{value.value.toFixed(1)}%
                </span>
            );
        }

        if (column.key === 'createdAt' || column.key === 'updatedAt') {
            if (!value) return '-';
            const { display, tooltip } = formatDateTime(value);
            return <span title={tooltip}>{display}</span>;
        }

        // Handle editable columns
        if (EDITABLE_FIELDS[column.key]) {
            const rawValue = getRawValue(item, column.key);
            return renderEditableCell(item, column.key, value, rawValue);
        }

        return value;
    };

    // Extracted row renderer — rendered once per visible virtualizer slot.
    // Identical JSX to the prior inline map. Declared above the early-return so
    // the hook order stays stable.
    const renderRow = useCallback((item) => {
        const isExpanded = expandedRows.has(item.leadId);
        const entries = item.entries || [];
        const hasMultipleEntries = entries.length > 1;
        const sortedEntries = [...entries].sort((a, b) => {
            const dateA = a.submittedAt?._seconds || 0;
            const dateB = b.submittedAt?._seconds || 0;
            return dateB - dateA;
        });
        const hasPropertyId = !!item.propertyId;
        // In-flight lead (just queued/processing) — it has no propertyId yet, so
        // it would otherwise fall into the greyed-out "disabled" treatment that
        // reads as dead/broken. Give it its own live treatment instead (accent
        // bar + subtle tint, see Leads.css) and a progress cursor.
        // Still mid-pipeline (enrichment or the phase-2 ARV window) — see isLeadWorking.
        // Shared with Leads.handleLeadClick (map/mobile cards) so both gate identically.
        const isWorking = isLeadWorking(item);
        // Openable only once it has a propertyId AND is no longer being analyzed.
        const canOpen = hasPropertyId && !isWorking;
        const actionsIsOpen = actionsOpenForLead === item.leadId;
        const dealStatusIsOpen = dealStatusDropdownLead === item.leadId;
        const outreachIsOpen = outreachDropdownLead === item.leadId;
        const rowElevated = actionsIsOpen || dealStatusIsOpen || outreachIsOpen;
        const isRowArchived = item.isArchived === true;
        return (
            <>
                <div
                    className={`leads-table-row-wrapper bg-colour-white ${isExpanded ? 'leads-table-row-expanded' : ''} ${isWorking ? 'leads-table-row-processing' : (!hasPropertyId ? 'leads-table-row-disabled' : '')} ${isRowArchived ? 'leads-table-row-archived' : ''}`}
                    style={{ cursor: canOpen ? 'pointer' : (isWorking ? 'progress' : 'not-allowed'), ...(rowElevated ? { zIndex: 20, position: 'relative' } : {}) }}
                    onMouseEnter={warmLeadDetailChunk}
                    onMouseDown={(e) => primeOnPress(e, item, canOpen)}
                    onClick={(e) => {
                        setDealStatusDropdownLead(null);
                        setOutreachDropdownLead(null);
                        if (!opensTheRow(e)) return;
                        // Don't open a lead that's still being analyzed (no propertyId
                        // yet, or mid enrichment/underwriting) — see canOpen. Inline
                        // controls above still work; dropdowns already closed above.
                        if (!canOpen) return;
                        navigate(`/leads/${item.propertyId}`, { state: { leadsSearch: window.location.search } });
                        if (companyId && (item.leadReviewStatus === 'new' || !item.leadReviewStatus)) {
                            const db = getFirestore();
                            const leadRef = doc(db, "companies", companyId, "leads", item.leadId);
                            updateDoc(leadRef, {
                                'data.leadReviewStatus': 'viewed',
                                'data.updatedAt': new Date()
                            }).catch(err => console.error('Error marking lead viewed:', err));
                            if (onLeadUpdate) onLeadUpdate(item.leadId, 'leadReviewStatus', 'viewed');
                        }
                        if (onRowClick) onRowClick(e, item);
                    }}
                >
                    <div className="leads-table-row" style={{ gridTemplateColumns: gridColumns }}>
                        {displayColumns.map(column => (
                            <div
                                key={column.key}
                                className={`leads-table-cell body-regular colour-primary ${(column.key === 'motivation' || column.key === 'dealStatus') ? 'leads-table-cell-wrap' : ''} ${column.key === 'distressSignals' ? 'leads-table-cell-distress' : ''} ${column.key === 'image' ? 'leads-image-cell' : ''}`}
                            >
                                {renderCell(item, column)}
                            </div>
                        ))}
                    </div>
                    <div className="leads-actions-sticky">
                        <div className="leads-actions-cell">
                            <ActionsKebab
                                isOpen={actionsIsOpen}
                                onToggle={() => {
                                    setDealStatusDropdownLead(null);
                                    setOutreachDropdownLead(null);
                                    if (onActionsOpenChange) onActionsOpenChange(actionsIsOpen ? null : item.leadId);
                                }}
                                isArchived={isArchivedView || isRowArchived}
                                onArchive={() => {
                                    if (onActionsOpenChange) onActionsOpenChange(null);
                                    if (onArchiveLead) onArchiveLead(item.leadId);
                                }}
                                onRestore={() => {
                                    if (onActionsOpenChange) onActionsOpenChange(null);
                                    if (onRestoreLead) onRestoreLead(item.leadId);
                                }}
                            />
                        </div>
                    </div>
                </div>
                {isExpanded && hasMultipleEntries && sortedEntries.map((entry, entryIndex) => (
                    <div key={entry.entryId || `entry-${entryIndex}`} className="leads-entry-row">
                        <div className="leads-entry-cell leads-entry-sender">
                            <span className="leads-entry-indicator">↳</span>
                            <span className="body-regular colour-primary">
                                {entry.sender?.name || entry.sender?.email || 'Main contact unknown'}
                            </span>
                        </div>
                        <div className="leads-entry-cell leads-entry-spacer"></div>
                        <div className="leads-entry-cell leads-entry-price body-regular colour-primary">
                            {entry.askingPrice ? formatCurrency(entry.askingPrice) : '-'}
                        </div>
                        <div className="leads-entry-cell leads-entry-date body-regular colour-secondary">
                            {(() => { const { display, tooltip } = formatDateTime(entry.submittedAt); return <span title={tooltip}>{display}</span>; })()}
                        </div>
                    </div>
                ))}
            </>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedRows, actionsOpenForLead, dealStatusDropdownLead, outreachDropdownLead, isArchivedView, gridColumns, displayColumns, companyId, navigate, onRowClick, onLeadUpdate, onArchiveLead, onRestoreLead, onActionsOpenChange, warmLeadDetailChunk, primeOnPress]);

    // Zero state - show when not loading and no data
    if (!loading && (!data || data.length === 0)) {
        return (
            <div className="leads-table-container">
                <div className="leads-table-zero-state">
                    <p className="body-regular colour-secondary">
                        Contact us to setup Direct to Seller leads or Property leads
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="leads-table-container" ref={scrollParentRef}>
            {refetching && <div className="leads-table-refetch-bar" aria-hidden="true" />}
            <div className="leads-table">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    measuring={{
                        droppable: {
                            strategy: MeasuringStrategy.Always
                        }
                    }}
                    autoScroll={{
                        enabled: true,
                        threshold: { x: 0.1, y: 0 },
                        acceleration: 10
                    }}
                >
                    <SortableContext
                        items={displayColumns.map(c => c.key)}
                        strategy={horizontalListSortingStrategy}
                    >
                        <div className="leads-table-header-wrapper">
                            <div className="leads-table-header" style={{ gridTemplateColumns: gridColumns }}>
                                {displayColumns.map(column => (
                                    <DraggableHeader
                                        key={column.key}
                                        column={column}
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection}
                                        onSort={onSort}
                                    />
                                ))}
                            </div>
                        </div>
                    </SortableContext>
                </DndContext>
                {/* Virtualized rows. Only what's in the viewport (+ overscan)
                    actually mounts; absolute positioning keeps scroll math
                    simple while letting React still render real DOM nodes. */}
                <div
                    className="leads-virtual-list"
                    style={{ height: rowVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}
                >
                    {rowVirtualizer.getVirtualItems().map(virtualRow => {
                        const item = data[virtualRow.index];
                        if (!item) return null;
                        return (
                            <div
                                key={item.leadId || virtualRow.index}
                                data-index={virtualRow.index}
                                ref={rowVirtualizer.measureElement}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${virtualRow.start}px)`
                                }}
                            >
                                {renderRow(item)}
                            </div>
                        );
                    })}
                </div>
                {actionsOpenForLead && (
                    <ExitScreen clickScreen={() => onActionsOpenChange(null)} />
                )}
            </div>
        </div>
    );
}

// Export standard columns and deal status config for use in parent components
export { STANDARD_COLUMNS, DEAL_STATUS_CONFIG, DEAL_STATUS_OPTIONS };
export default LeadsTable;
